/* global document */

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Box, Heading3, lessThan, StackView, Text } from 'localmed-core';
import React, { useEffect } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DiAddToCalendarLink } from '../../../../../shared/components/AddToCalendarLink';
import { Link } from '../../../../../shared/components/DiComponents';
import TrackClick from '../../../../../shared/components/TrackClick';
import { DATE_FORMAT } from '../../../../../shared/constants';
import DIThumbsUp from '../../../../../shared/icons/DIThumbsUp';
import { formatDate, formatDateOnly } from '../../../../../shared/utils/formatCalendarTime';
import getCalendarEvent from '../../../../../shared/utils/getCalendarEvent';
import type { ThemeProps } from '../../../../../types';
import { ANNOUNCEMENT_TYPES } from '../../../constants';
import { IStepProps } from '../schedulingWizard';
import { Wrapper } from '../styles';

interface Props extends IStepProps {
  intl: IntlShape;
}

const StyledDIThumbsUp = styled(DIThumbsUp)(
  css`
    width: 300px;
    height: 300px;
  `,
  lessThan(
    'lg',
    css`
      width: 150px;
      height: 150px;
    `
  )
);

const FloatingBox = styled(Box)(
  ({ theme }: ThemeProps) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    text-align: center;
    min-width: 400px;
    background: ${theme.colors.white};
    border-radius: ${theme.radii.xl};
    padding: ${theme.space.md};
    box-shadow: ${theme.elevations[2]};
    transform: translate(-50%, -50%);
  `,
  lessThan(
    'lg',
    css`
      display: none;
    `
  )
);

function SchedulingBookedStep({ wizard, intl }: Props) {
  useEffect(() => {
    if (wizard && wizard.formProps.values.trackSchedulingEvent) {
      wizard.formProps.values.trackSchedulingEvent(
        'Scheduling Booked Screen',
        'SchedulingBookedStep'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!wizard) return null;

  const { opening, provider, appointment, office, patientGivenName } = wizard.formProps.values;

  const calendarEvent = getCalendarEvent({
    patientName: patientGivenName,
    provider,
    office,
    topic: 'appointment',
    id: appointment.id,
    time: opening.time,
  });

  const { announcementEnabled, announcement } = office || {};
  const covidProceduresLink =
    announcementEnabled &&
    announcement?.type === ANNOUNCEMENT_TYPES.COVID_19 &&
    announcement.moreUrl;

  const Actions = () => (
    <>
      <DiAddToCalendarLink
        id={`add-to-calendar-link-${appointment?.id}`}
        calendarEvent={calendarEvent}
        trackingEventComponentName="SchedulingBookedStep"
        full
      />
      {covidProceduresLink && (
        <TrackClick
          event="Clicked Covid Procedures Link"
          eventProperties={{ component: 'SchedulingBookedStep' }}
        >
          {(getTrackingProps) => (
            <Link
              href={covidProceduresLink}
              rel="noopener noreferrer"
              target="_blank"
              {...getTrackingProps()}
            >
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.covidPoliciesLinkText"
                defaultMessage="Our Covid-19 Policies and Procedures"
              />
            </Link>
          )}
        </TrackClick>
      )}
    </>
  );

  return (
    <Wrapper justifyContent="center" alignItems="center">
      <Box
        display="flex"
        alignItems="center"
        textAlign={{ _: 'center', lg: 'inherit' }}
        flexDirection={{ _: 'column', lg: 'row' }}
        maxWidth="100%"
      >
        <Box position="relative" minWidth={{ _: 'initial', lg: '400px' }} textAlign="center">
          <StyledDIThumbsUp />
          <FloatingBox>
            <Text>
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.appointmentDateTimeMessage"
                defaultMessage="Appointment Date and Time"
              />
            </Text>
            <Text>
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.appointmentDateTimeDetails"
                defaultMessage="{date} | {time}"
                values={{
                  date: formatDate(intl, opening.time, null, {
                    intl: DATE_FORMAT.LONG,
                    fallback: DATE_FORMAT.LONG_FALLBACK,
                  }),
                  time: formatDateOnly(opening.time, 'p'),
                }}
              />
            </Text>
          </FloatingBox>
        </Box>
        <Box ml="xxl" display={{ _: 'none', lg: 'block' }}>
          <StackView spacing="lg" display={{ _: 'none', lg: 'inherit' }}>
            <Heading3>
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.appointmentBookedTitle"
                defaultMessage="{patientGivenName}, Your Appointment Is Scheduled!"
                values={{
                  patientGivenName,
                }}
              />
            </Heading3>
            <Text>
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.appointmentBookedMessage"
                defaultMessage="Your appointment is scheduled! We look forward to seeing you soon. You can click the
                button below to add your appointment to your calendar."
              />
            </Text>
            <Actions />
          </StackView>
        </Box>
        <Box display={{ _: 'initial', lg: 'none' }}>
          <StackView spacing="lg" display={{ _: 'initial', lg: 'none' }}>
            <Heading3 marginTop="xxl">
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.appointmentDetailsTitle"
                defaultMessage="Appointment Scheduled!"
              />
            </Heading3>
            <Text>
              <FormattedMessage
                id="scheduling.SchedulingBookedStep.appointmentDetails"
                defaultMessage="We look forward to seeing you on {date} at {time}."
                values={{
                  date: formatDate(intl, opening.time, null, {
                    intl: DATE_FORMAT.LONG,
                    fallback: DATE_FORMAT.LONG_FALLBACK,
                  }),
                  time: formatDateOnly(opening.time, 'p'),
                }}
              />
            </Text>
            <Actions />
          </StackView>
        </Box>
      </Box>
    </Wrapper>
  );
}

export default injectIntl(SchedulingBookedStep);
